/**
 * Stage timeline utilities — pure functions for computing
 * stage bar segments from stage data.
 */

export interface StageSummary {
  name: string
  status: 'success' | 'failed' | 'skipped' | 'running'
  durationMs?: number
  startedAt?: Date | string
  isFinally: boolean
}

export interface StageSegment {
  name: string
  status: StageSummary['status']
  durationMs?: number
  widthPercent: number
  isFinally: boolean
  label: string
  color: string
}

/**
 * A curated palette of muted, low-saturation colors.
 * Designed to be visually distinct but harmonious — more like
 * tinted grays than a rainbow. Works on both dark and light backgrounds.
 *
 * Each entry is [hue, saturation, lightness].
 */
const STAGE_PALETTE: [number, number, number][] = [
  [210, 35, 52],  // slate blue
  [160, 30, 48],  // sage
  [35,  35, 50],  // warm sand
  [250, 25, 55],  // muted lavender
  [185, 30, 48],  // steel teal
  [20,  30, 52],  // terracotta
  [140, 25, 48],  // moss
  [280, 20, 52],  // dusty grape
]

/**
 * Deterministic hash of a stage name to a palette index.
 * Same name always produces the same color across builds.
 */
function hashName(name: string): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

/**
 * Get a deterministic HSL color string for a stage name.
 * Failed stages get a muted red, skipped stages get gray.
 * Otherwise the color is a muted tone derived from the name.
 */
export function stageColor(name: string, status: StageSummary['status']): string {
  if (status === 'skipped') return 'hsl(0, 0%, 45%)'
  if (status === 'failed') return 'hsl(0, 45%, 50%)'
  const [h, s, l] = STAGE_PALETTE[hashName(name) % STAGE_PALETTE.length]
  if (status === 'running') return `hsl(${h}, ${s + 10}%, ${l + 5}%)`
  return `hsl(${h}, ${s}%, ${l}%)`
}

/**
 * Format a duration in milliseconds to a compact human-readable string.
 * Examples: 800 → "<1s", 1200 → "1s", 65000 → "1m 5s", 3725000 → "1h 2m"
 */
export function formatStageDuration(ms: number): string {
  if (ms <= 0) return '0s'
  if (ms < 1000) return '<1s'

  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    const remainMinutes = minutes % 60
    return remainMinutes > 0 ? `${hours}h ${remainMinutes}m` : `${hours}h`
  }
  if (minutes > 0) {
    const remainSeconds = seconds % 60
    return remainSeconds > 0 ? `${minutes}m ${remainSeconds}s` : `${minutes}m`
  }
  return `${seconds}s`
}

/**
 * Compute visual segments for a stage bar from raw stage data.
 * Each segment gets a width proportional to its duration relative to the total.
 * Skipped stages are excluded. Running stages use runningElapsedMs for width
 * and label so their bar grows with time.
 */
export function computeSegments(stages: StageSummary[], runningElapsedMs?: number): StageSegment[] {
  // Filter out skipped stages — they add noise without value
  const visible = stages.filter(s => s.status !== 'skipped')
  if (visible.length === 0) return []

  // Use runningElapsedMs as effective duration for running stages
  function effectiveDuration(s: StageSummary): number {
    if (s.status === 'running') return runningElapsedMs ?? 0
    return s.durationMs ?? 0
  }

  const totalMs = visible.reduce((sum, s) => sum + effectiveDuration(s), 0)

  // If total is zero (all instant or just started), distribute evenly
  if (totalMs === 0) {
    const evenWidth = 100 / visible.length
    return visible.map(s => ({
      name: s.name,
      status: s.status,
      durationMs: s.durationMs,
      widthPercent: evenWidth,
      isFinally: s.isFinally,
      label: s.status === 'running' ? formatStageDuration(runningElapsedMs ?? 0) : formatStageDuration(s.durationMs ?? 0),
      color: stageColor(s.name, s.status),
    }))
  }

  const MIN_WIDTH = 4 // minimum percent for visibility

  return visible.map(s => {
    const proportion = effectiveDuration(s) / totalMs
    let widthPercent = proportion * 100
    // Ensure all stages are visible
    if (widthPercent < MIN_WIDTH && visible.length > 1) {
      widthPercent = MIN_WIDTH
    }

    return {
      name: s.name,
      status: s.status,
      durationMs: s.durationMs,
      widthPercent,
      isFinally: s.isFinally,
      label: s.status === 'running' ? formatStageDuration(runningElapsedMs ?? 0) : formatStageDuration(s.durationMs ?? 0),
      color: stageColor(s.name, s.status),
    }
  })
}
